import dayjs, { ManipulateType } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import { IDateService, TimeUnit } from './IDateService'

const padStart = function padStart (string, length, pad) {
  const s = String(string)
  if (!s || s.length >= length) return string
  return '' + Array(length + 1 - s.length).join(pad) + string
}

const padZoneStr = function padZoneStr (instance) {
  const negMinutes = -instance.utcOffset()
  const minutes = Math.abs(negMinutes)
  const hourOffset = Math.floor(minutes / 60)
  const minuteOffset = minutes % 60
  return '' + (negMinutes <= 0 ? '+' : '-') + padStart(hourOffset, 2, '0') + ':' + padStart(minuteOffset, 2, '0')
}

const Utils = {
  s: padStart,
  z: padZoneStr
}

const C = {
  REGEX_FORMAT: /\[([^\]]+)]|Y{2,4}|M{1,4}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|a|A|m{1,2}|s{1,2}|Z{1,2}|SSS/g,
  INVALID_DATE_STRING: 'Invalid Date',
  FORMAT_DEFAULT: 'YYYY-MM-DDTHH:mm:ssZ'
}

export function format (formatStr) {
  if (!this.isValid()) return C.INVALID_DATE_STRING

  const str = formatStr || C.FORMAT_DEFAULT
  const zoneStr = Utils.z(this)
  const locale = this.$locale()
  const { $H, $m, $M } = this
  const {
    weekdays, months, meridiem
  } = locale
  const getShort = (arr, index, full?, length?) => (
    (arr && (arr[index] || arr(this, str))) || full[index].substr(0, length)
  )
  const get$H = num => (
    Utils.s($H % 12 || 12, num, '0')
  )

  const meridiemFunc = meridiem || ((hour, minute, isLowercase) => {
    const m = (hour < 12 ? 'AM' : 'PM')
    return isLowercase ? m.toLowerCase() : m
  })

  const matches = {
    YY: String(this.$y).slice(-2),
    YYYY: String(this.$y).padStart(4, '0'), // override this
    M: $M + 1,
    MM: Utils.s($M + 1, 2, '0'),
    MMM: getShort(locale.monthsShort, $M, months, 3),
    MMMM: getShort(months, $M),
    D: this.$D,
    DD: Utils.s(this.$D, 2, '0'),
    d: String(this.$W),
    dd: getShort(locale.weekdaysMin, this.$W, weekdays, 2),
    ddd: getShort(locale.weekdaysShort, this.$W, weekdays, 3),
    dddd: weekdays[this.$W],
    H: String($H),
    HH: Utils.s($H, 2, '0'),
    h: get$H(1),
    hh: get$H(2),
    a: meridiemFunc($H, $m, true),
    A: meridiemFunc($H, $m, false),
    m: String($m),
    mm: Utils.s($m, 2, '0'),
    s: String(this.$s),
    ss: Utils.s(this.$s, 2, '0'),
    SSS: Utils.s(this.$ms, 3, '0'),
    Z: zoneStr // 'ZZ' logic below
  }

  return str.replace(C.REGEX_FORMAT, (match, $1) => $1 || matches[match] || zoneStr.replace(':', '')) // 'ZZ'
}

export class DateService implements IDateService {
  private timezone = 'Asia/Singapore'

  constructor () {
    this.timezone = 'Asia/Singapore'
    dayjs.extend(customParseFormat)
    dayjs.extend(utc)
    dayjs.extend(timezonePlugin)
    dayjs.prototype.format = format
  }

  // format (date: string | number | Date, format: string) {
  //   if (typeof date === 'string') {
  //     // there is case that when the timestamp return is a string type, need to parse to number first
  //     // there is also possibility that date passed as a format of '01-01-2024',
  //     // in this case we cannot parse it to number, we should skip this
  //     const numberDate = Number(date)
  //     if (numberDate) { // mean it is timestamp in string
  //       return dayjs(Number(date)).tz(this.timezone).format(format)
  //     }
  //   }

  //   return dayjs(date).tz(this.timezone).format(format)
  // }

  format (date: string | number | Date, format: string) {
    let dateObj: dayjs.Dayjs

    if (typeof date === 'string') {
      // there is case that when the timestamp return is a string type, need to parse to number first
      // there is also possibility that date passed as a format of '01-01-2024',
      // in this case we cannot parse it to number, we should skip this
      const numberDate = Number(date)
      if (numberDate) { // mean it is timestamp in string
        dateObj = dayjs.utc(numberDate)
      } else {
        dateObj = dayjs.utc(date)
      }
    } else {
      dateObj = dayjs.utc(date)
    }

    // Check if date is before 1982
    const transitionDate = dayjs.utc('1982-01-01')
    if (dateObj.isBefore(transitionDate)) {
      // Add 30 minutes to compensate for the difference
      return dateObj.add(30, 'minute').tz(this.timezone).format(format)
    }

    return dateObj.tz(this.timezone).format(format)
  }

  calculateAge (referDate: Date, dob: Date, asFloat = false) {
    return dayjs(referDate).tz(this.timezone).diff(dayjs(dob), 'year', asFloat)
  }

  toUnixMilliseconds (date: string, format: string) {
    return dayjs(date, format).tz(this.timezone).valueOf()
  }

  isValid (date: string, format: string) {
    return dayjs(date, format).tz(this.timezone).format(format) === date
  }

  isValidDate (date: string | number, format: string) {
    const formattedDate = this.format(date, format)
    return dayjs(formattedDate, format, true).isValid()
  }

  add (date: string | number | Date, timeUnit: TimeUnit, amount: number) {
    return dayjs(date).tz(this.timezone).add(amount, timeUnit as ManipulateType).toDate()
  }

  subtract (date: string | number | Date, timeUnit: TimeUnit, amount: number) {
    return dayjs(date).tz(this.timezone).subtract(amount, timeUnit as ManipulateType).toDate()
  }

  getDateOfMonthOfSpecificDate (date: number, specificDate?: string | number | Date) {
    return specificDate ? dayjs(specificDate).tz(this.timezone).date(date).toDate() : dayjs().tz(this.timezone).date(date).toDate()
  }

  setHour (date: string | number | Date, hour?: number, minute?: number, second?: number, millisec?: number): number {
    const dayJsDate = dayjs(date).tz(this.timezone)
    const dateHour = hour || dayJsDate.get('hour')
    const dateMinute = minute || dayJsDate.get('minute')
    const dateSecond = second || dayJsDate.get('second')
    const dateMillisec = millisec || dayJsDate.get('millisecond')
    return dayJsDate
      .set('hour', dateHour)
      .set('minute', dateMinute)
      .set('second', dateSecond)
      .set('millisecond', dateMillisec)
      .valueOf()
  }

  get (date: string | number | Date, timeUnit: Exclude<TimeUnit, 'week'>): number {
    return dayjs(date).tz(this.timezone).get(timeUnit)
  }

  isInThePastOfDate (dateToCheck: string | number | Date, dateToCompareWith: string | number | Date, compareDayOnly = true): boolean {
    const dateToCompareInFormat = compareDayOnly ? dayjs(dateToCompareWith).tz(this.timezone).format('YYYY-MM-DD') : dayjs(dateToCompareWith).tz(this.timezone)
    return dayjs(dateToCheck).tz(this.timezone).isBefore(dateToCompareInFormat)
  }

  difference (dateToCheck: string | number | Date, dateToCompareWith: string | number | Date): string {
    dayjs.extend(duration)
    dayjs.extend(relativeTime)
    return dayjs.duration(dayjs(dateToCheck).diff(dayjs(dateToCompareWith))).humanize()
  }
}
